import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  KubernetesRegistryFormData,
  KubernetesRegistryPolicyPayload,
  KUBERNETES_REGISTRY_INITIAL_VALUES,
} from './types';

/**
 * Hook to get initial values for Kubernetes Registry Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useKubernetesRegistryInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesRegistryFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_REGISTRY_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!data || typeof data !== 'object') {
      return KUBERNETES_REGISTRY_INITIAL_VALUES;
    }
    if (!('RegistryAccessPolicies' in data)) {
      return KUBERNETES_REGISTRY_INITIAL_VALUES;
    }

    const policyData = data as KubernetesRegistryPolicyPayload;
    const accessRows: KubernetesRegistryFormData['accessRows'] = [];

    // Convert per-registry structure to form rows
    const regPolicies = policyData.RegistryAccessPolicies;
    if (regPolicies) {
      Object.entries(regPolicies).forEach(([registryIdStr, registryPolicy]) => {
        const registryId = parseInt(registryIdStr, 10);
        const namespaces =
          registryPolicy.Namespaces?.map((ns) => ns.value) || [];

        accessRows.push({
          registryId,
          namespaces,
        });
      });
    }

    return {
      accessRows,
    };
  }, [policy]);
}
